<template>
  <div class="container mt-4">

    <!-- TOP BAR -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="mb-0">
        
        {{ $t('salaryReview.title') }}
      </h4>

      <!-- Admin only -->
    <button
  v-if="isAdmin"
  class="btn btn-sm text-white"
  style="background:#b03a45;"
  @click="goToMySalaryAdvances"
>
  💰 {{ $t('salaryAdvance.mySalaryAdvances') }}  
</button>

    </div>

    <div class="card shadow border-0">
      <div
        class="card-header text-white fw-bold"
        style="background: linear-gradient(195deg, #4b5563, #8d2e38);"
      >
        <i class="material-icons me-2">request_quote</i>
        {{ $t('salaryReview.header') }}
      </div>

      <!-- Avertissement RRH -->
      <div class="p-3" v-if="!isHRHead">
        <div class="alert alert-warning mb-0">
          {{ $t('salaryReview.warning') }}
        </div>
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="text-uppercase small" style="background-color:#f8f9fa;">
            <tr>
              <th>{{ $t('salaryReview.table.employee') }}</th>
              <th>{{ $t('salaryReview.table.amount') }}</th>
              <th>{{ $t('salaryReview.table.type') }}</th>
              <th>{{ $t('salaryReview.table.reason') }}</th>
              <th>{{ $t('salaryReview.table.status') }}</th>
              <th>{{ $t('salaryReview.table.supervisor') }}</th>
              <th>{{ $t('salaryReview.table.actions') }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="advance in salaryAdvances" :key="advance.id">
              <!-- Employee -->
              <td>
                {{ advance.user?.firstname }} {{ advance.user?.lastname }}
              </td>

              <!-- Amount -->
              <td>{{ advance.amount }} TND</td>

              <!-- Type -->
              <td>{{ advance.type_advance || '—' }}</td>

              <!-- Reason -->
              <td>{{ advance.reason || '—' }}</td>

              <!-- Status -->
              <td>
                <span :class="['badge', statusClass(advance.status)]">
                  {{ advance.status }}
                </span>
              </td>

              <!-- Supervisor -->
              <td>
                <span v-if="advance.supervisor">
                  {{ advance.supervisor.firstname }} {{ advance.supervisor.lastname }}
                </span>
                <span v-else class="text-muted">—</span>
              </td>

              <!-- Actions -->
              <td>
                <template v-if="isHRHead">
                  <button
                    v-if="advance.status === 'Pending'"
                    class="btn btn-success btn-sm me-1"
                    :disabled="actioningId === advance.id"
                    @click="onApprove(advance.id)"
                  >
                    ✔ {{ $t('salaryReview.actions.approve') }}
                  </button>

                  <button
                    v-if="advance.status === 'Pending'"
                    class="btn btn-danger btn-sm"
                    :disabled="actioningId === advance.id"
                    @click="onReject(advance.id)"
                  >
                    ✘ {{ $t('salaryReview.actions.reject') }}
                  </button>
                </template>
              </td>
            </tr>

            <!-- Empty state -->
            <tr v-if="salaryAdvances.length === 0">
              <td colspan="7" class="text-center text-muted py-4">
                {{ $t('salaryReview.noRequests') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/api'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

// State
const salaryAdvances = ref([])
const actioningId = ref(null)

// User connecté
const user = JSON.parse(localStorage.getItem('user') || '{}')

// ✅ Admin detection (robuste)
const rolesNormalized = computed(() => {
  const r = user?.roles

  // roles: ["admin", "employee"]
  if (Array.isArray(r) && typeof r[0] === 'string') return r.map(x => String(x).toLowerCase())

  // roles: [{name:"admin"}, ...]
  if (Array.isArray(r) && typeof r[0] === 'object') {
    return r.map(x => String(x?.name || x?.role || '').toLowerCase()).filter(Boolean)
  }

  // role: "admin"
  if (typeof user?.role === 'string') return [user.role.toLowerCase()]

  return []
})

const isAdmin = computed(() => {
  return (
    rolesNormalized.value.includes('admin') ||
    rolesNormalized.value.includes('SuperAdmin') ||
    user?.is_admin === true ||
    user?.isAdmin === true
  )
})

// Vérifie si l’utilisateur est RRH / HR Head / Admin
const isHRHead = computed(() => {
  const pi = user?.professional_information || user?.professionalInformation
  const positionCode = pi?.position?.code
  const roles = user?.roles || []
  // roles peut être objets ou strings → on utilise rolesNormalized pour être safe
  return (
    positionCode === 'RRH' ||
    rolesNormalized.value.includes('rrh') ||
    rolesNormalized.value.includes('hr head') ||
    isAdmin.value
  )
})

// ✅ Button action: page "Request a Salary Advance" (capture)
const goToMySalaryAdvances = () => {
  router.push('/employee/salary-advances/request')
}

// 🔹 Charger la liste des avances
const fetchSalaryAdvances = async () => {
  try {
    const { data } = await axios.get('/employee/salary-advances/all')
    salaryAdvances.value = data.advances || []
  } catch (e) {
    toast.error(t('salaryReview.messages.loadError'))
    console.error(e)
  }
}

// 🔹 Approbation
const onApprove = async (id) => {
  if (!isHRHead.value) {
    toast.warning(t('salaryReview.messages.notAuthorizedApprove'))
    return
  }
  if (!confirm(t('salaryReview.messages.confirmApprove'))) return
  actioningId.value = id
  try {
    await axios.post(`/employee/salary-advances/${id}/approve`)
    toast.success(t('salaryReview.messages.approveSuccess'))
    await fetchSalaryAdvances()
  } catch (e) {
    if (e.response?.status === 403) {
      toast.error(t('salaryReview.messages.forbidden'))
    } else {
      toast.error(t('salaryReview.messages.approveFail'))
    }
    console.error(e)
  } finally {
    actioningId.value = null
  }
}

// 🔹 Rejet
const onReject = async (id) => {
  if (!isHRHead.value) {
    toast.warning(t('salaryReview.messages.notAuthorizedReject'))
    return
  }
  if (!confirm(t('salaryReview.messages.confirmReject'))) return
  actioningId.value = id
  try {
    await axios.post(`/employee/salary-advances/${id}/reject`)
    toast.success(t('salaryReview.messages.rejectSuccess'))
    await fetchSalaryAdvances()
  } catch (e) {
    if (e.response?.status === 403) {
      toast.error(t('salaryReview.messages.forbidden'))
    } else {
      toast.error(t('salaryReview.messages.rejectFail'))
    }
    console.error(e)
  } finally {
    actioningId.value = null
  }
}

// 🔹 Classes de statut
const statusClass = (status) => {
  switch (status) {
    case 'Approved': return 'bg-success text-white'
    case 'Rejected': return 'bg-danger text-white'
    case 'Pending': return 'bg-warning text-dark'
    default: return 'bg-secondary text-white'
  }
}

onMounted(fetchSalaryAdvances)
</script>

<style scoped>
.badge {
  padding: 0.4em 0.8em;
  border-radius: 0.5rem;
  text-transform: uppercase;
  font-size: 0.75rem;
}
.alert {
  font-size: 0.9rem;
}
</style>
